import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/SEOHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const aboutSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Fluffy Pets',
    url: `${SITE_URL}/about`,
    description:
      'Learn about Fluffy Pets — founded in 2015 by Dr. Sarah Johnson, our compassionate team provides grooming, boarding, veterinary, and training services for pets in San Francisco.',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Fluffy Pets',
      url: SITE_URL,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Fluffy Pets',
    url: SITE_URL,
    logo: 'https://static.readdy.ai/image/8ba2fb49baf129fe47398873ffc1b622/7319ff1316bfcbedde225c0cf8d58231.jpeg',
    foundingDate: '2015',
    founder: {
      '@type': 'Person',
      name: 'Sarah Johnson',
      jobTitle: 'Founder & Lead Veterinarian',
    },
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      value: 50,
    },
    description:
      'Fluffy Pets is a comprehensive pet care facility serving thousands of happy pets and their families since 2015.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '123 Pet Care Lane',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94102',
      addressCountry: 'US',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+15551234567',
      contactType: 'customer service',
      email: 'hello@fluffypets.com',
    },
  },
];

// ... existing code ...

export default function AboutPage() {
  const team = [
    {
      name: 'Sarah Johnson',
      role: 'Founder & Lead Veterinarian',
      image: 'https://public.readdy.ai/ai/img_res/002d193fff7d71e86c5c17f2d1540f0e.jpg',
      bio: '15+ years of veterinary experience with a passion for animal welfare'
    },
    {
      name: 'Michael Chen',
      role: 'Senior Groomer',
      image: 'https://readdy.ai/api/search-image?query=professional%20male%20pet%20groomer%20in%20modern%20salon%20holding%20grooming%20tools%20smiling%20confidently%20at%20camera%20with%20purple%20and%20pink%20gradient%20lighting%20professional%20portrait%20photography%20high%20quality&width=400&height=500&seq=about-team-2&orientation=portrait',
      bio: 'Award-winning groomer specializing in breed-specific styling'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Pet Trainer & Behaviorist',
      image: 'https://readdy.ai/api/search-image?query=professional%20female%20dog%20trainer%20in%20outdoor%20setting%20with%20training%20equipment%20smiling%20warmly%20at%20camera%20with%20purple%20and%20pink%20gradient%20lighting%20professional%20portrait%20photography%20high%20quality&width=400&height=500&seq=about-team-3&orientation=portrait',
      bio: 'Certified trainer with expertise in positive reinforcement methods'
    },
    {
      name: 'David Thompson',
      role: 'Boarding Manager',
      image: 'https://readdy.ai/api/search-image?query=professional%20male%20pet%20care%20manager%20in%20modern%20facility%20with%20happy%20dogs%20smiling%20confidently%20at%20camera%20with%20purple%20and%20pink%20gradient%20lighting%20professional%20portrait%20photography%20high%20quality&width=400&height=500&seq=about-team-4&orientation=portrait',
      bio: 'Dedicated to creating a home-away-from-home for every pet'
    }
  ];

  const values = [
    {
      icon: 'ri-heart-3-line',
      title: 'Compassionate Care',
      description: 'Every pet receives individualized attention and love, treating them as members of our own family with genuine compassion and understanding.'
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Excellence',
      description: 'We maintain the highest standards in pet care, continuously improving our services and staying updated with the latest industry practices.'
    },
    {
      icon: 'ri-team-line',
      title: 'Community',
      description: 'Building lasting relationships with pet owners and creating a supportive community where everyone feels welcome and valued.'
    },
    {
      icon: 'ri-leaf-line',
      title: 'Sustainability',
      description: 'Using eco-friendly products and practices to ensure a healthier environment for pets, people, and our planet.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="About Fluffy Pets | Our Story, Team &amp; Values - Pet Care San Francisco"
        description="Meet the passionate team behind Fluffy Pets. Founded in 2015 by Dr. Sarah Johnson, we've grown to serve 10,000+ pets with expert grooming, boarding, and veterinary care in San Francisco."
        keywords="about fluffy pets, pet care team San Francisco, veterinarian Sarah Johnson, pet grooming professionals"
        canonical={`${SITE_URL}/about`}
        schema={aboutSchema}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-purple-300/30 to-pink-300/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-gradient-to-tr from-pink-300/30 to-purple-300/30 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-black mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            About Fluffy Pets
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Where passion meets expertise in pet care. We're dedicated to providing exceptional services that keep your furry friends happy, healthy, and thriving.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-black mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Our Story
              </h2>
              <div className="space-y-6 text-gray-600 leading-relaxed text-lg">
                <p>
                  Founded in 2015, Fluffy Pets began with a simple mission: to create a place where pets receive the same level of care and attention that we give to our own beloved animals. What started as a small grooming salon has grown into a comprehensive pet care facility serving thousands of happy pets and their families.
                </p>
                <p>
                  Our founder, Dr. Sarah Johnson, recognized the need for a pet care center that combines medical expertise with genuine love for animals. After years of working in traditional veterinary clinics, she envisioned a space where pets would feel comfortable, safe, and truly cared for.
                </p>
                <p>
                  Today, Fluffy Pets is proud to be a trusted name in pet care, offering everything from routine grooming to advanced veterinary services. Our team of passionate professionals treats every pet as if they were their own, ensuring the highest quality care in a warm, welcoming environment.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-[600px] rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20bright%20pet%20care%20facility%20interior%20with%20happy%20dogs%20and%20cats%20being%20cared%20for%20by%20professional%20staff%20in%20clean%20spacious%20environment%20with%20purple%20and%20pink%20accent%20lighting%20warm%20welcoming%20atmosphere%20high%20quality%20photography&width=700&height=600&seq=about-story-1&orientation=landscape"
                  alt="Fluffy Pets Facility"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do at Fluffy Pets
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl mb-6">
                  <i className={`${value.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 id="team" className="text-5xl font-bold text-center mb-4 bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] bg-clip-text text-transparent" style={{ fontFamily: 'Pacifico, cursive' }}>
            Meet Our Team
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Passionate professionals dedicated to your pet's wellbeing
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div 
                key={index}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative w-full h-80 overflow-hidden">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-gray-800">{member.name}</h3>
                  <p className="text-purple-600 font-semibold mb-3">{member.role}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { number: '10,000+', label: 'Happy Pets' },
              { number: '8+', label: 'Years Experience' },
              { number: '50+', label: 'Team Members' },
              { number: '15+', label: 'Services Offered' }
            ].map((stat, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="text-5xl font-black mb-3 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-semibold text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Join the Fluffy Pets Family
          </h2>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed">
            Experience the difference that genuine care and expertise can make in your pet's life. We can't wait to meet you and your furry friend!
          </p>
          <a 
            href="/booking"
            className="inline-block px-10 py-5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-lg font-bold rounded-full hover:shadow-2xl transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Book Your First Visit
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
